import { Currency } from 'server/types';
export const AIP_COURSE_ID = 'd8f24b30-7c1e-4a9f-9d3e-6b5a8f2c1e70';

// "Founding rate" framing for AI Playground. The membership is positioned at
// the anchor / "now" rate while current and early members pay the founding
// rate stored on the active price row. The anchor is DISPLAY-ONLY — it does
// not change what Stripe charges. Mirrors the cancellation-flow copy in
// subscription-status.tsx; update both together if the positioned rate changes.
//
// One per currency, because an anchor is a positioning decision rather than a
// conversion: CA$270 was set to sit beside CA$132 the way $200 sits beside
// $97. A currency with no anchor shows the price on its own — borrowing
// another currency's figure would advertise a discount nobody agreed to.
//
// Held in cents so each screen formats it for its own context: the anchor and
// the real price are always the same currency, so within one rendering they
// can both use the reader's own locale.
const aipAnchorPrices: Record<string, number> = {
  USD: 20000,
  CAD: 27000,
};

export function aipAnchorPrice(
  currency?: string,
): { priceInCents: number; currency: Currency } | undefined {
  if (!currency) {
    return;
  }
  const upper = currency.toUpperCase();
  const priceInCents = aipAnchorPrices[upper];
  return priceInCents ? { priceInCents, currency: upper as Currency } : undefined;
}

// AIP "backup offer" for repeat-trialers — people who already used an AIP free
// trial and cancelled, so they're not eligible for another. Instead of the bare
// full-price continuation, they're offered a discounted first month: a once-only
// amount-off coupon. The coupon is applied SERVER-SIDE ONLY (never
// client-supplied), so the prior-trial eligibility gate can't be bypassed via
// the public checkout. The internal pmts.coupons.id is fixed across
// environments; the underlying Stripe coupon differs per env (test vs live).
// See server/payments/checkout.ts (internalCouponId) and
// resolveAipBackupCoupon in server/dbx/aip-trial-stack.ts.
//
// One per currency, because Stripe refuses an amount-off coupon against a price
// in another currency, and each amount is its own pricing decision rather than
// a conversion: US$50 off US$97 → US$47, CA$68 off CA$132 → CA$64.
const aipBackupOffers: Record<string, { couponId: string; amountOffCents: number }> = {
  USD: { couponId: 'coup-aip-backup-offer', amountOffCents: 5000 },
  CAD: { couponId: 'coup-aip-backup-offer-cad', amountOffCents: 6800 },
};

export function aipBackupOfferFor(
  currency?: string,
): { couponId: string; amountOffCents: number; currency: Currency } | undefined {
  const upper = (currency || 'USD').toUpperCase();
  const offer = aipBackupOffers[upper];
  return offer ? { ...offer, currency: upper as Currency } : undefined;
}

export const COMPANY_NAME_FIELD_ID = 'c0000000-0000-4000-a000-000000000001';

// Recipient for the Monday Champion Activity digest. Hardcoded for v1 — Barb
// is the only stakeholder receiving this report. Move to a join table when a
// second recipient is needed.
export const BARB_USER_ID = 'ed2ca36c-08eb-4cfe-bb92-eca0d8af77b9';

// Recipients for the monthly AIP Cohort Retention digest (Bhoomi + Barb).
// Hardcoded for v1 — move to a join table when the list grows.
export const BHOOMI_USER_ID = '1e361812-d014-40d0-bb57-d1373ecbd2b2';
export const COHORT_RETENTION_DIGEST_RECIPIENTS = [BHOOMI_USER_ID, BARB_USER_ID];

// Maps user IDs to staff titles shown on community posts.
export const MIRASEE_STAFF_TITLES: Record<string, string> = {
  '7564afc4-3aed-4c89-a7c9-56999dac3802': 'Founder & CEO',
  'd9342227-f88d-4330-9ea9-87891748a2cf': 'Head of Product',
};

// Internal/test courses that should be hidden from analytics surfaces (the
// email schedule agenda, etc.). Receipts are still written so cron sends
// stay idempotent — only the read path filters these out. Add a course id
// here when a guide spins up a personal sandbox they don't want polluting
// the engagement view.
export const ANALYTICS_EXCLUDED_COURSE_IDS: ReadonlySet<UUID> = new Set([
  '72ca022f-2383-4123-b27f-e1ba19d37ef2', // Test Enrollment (Abe's sandbox)
]);

// SimpleCheck consent notice. Shown under the email field on campaign checkout
// pages that turn on `simpleCheckConsent` (e.g. the AIS checkout). Wording is
// compliance copy supplied by Mirasee marketing — keep it verbatim, two
// paragraphs. When the purchase goes through, notifyOntraport writes the time,
// this exact text and the buyer's IP to the OP contact. Fields are looked up
// by NAME because ids (Mirasee's are f2952/f2953) differ per OP account.
// Wording from Lizzie (via Danny/Ari), 2026-10-14. Changing it starts a new
// "version": consents already recorded keep the text they agreed to.
export const SIMPLECHECK_CONSENT_PARAGRAPHS = [
  'By entering your email address above, you are requesting updates about upcoming trainings and advice to grow your business. You can opt out at any time, and we’ll never rent or sell your email address.',
  'By submitting my contact information I certify that I’m 18 years of age or older and that the information provided is my own. I authorize Mirasee to contact me in forms including emails, phone calls, text messages, and voice messages.',
  'I also give written authorization to Mirasee to obtain a copy of my consumer report to personalize my experience, and I understand this is optional and not required to make a purchase.',
] as const;
export const SIMPLECHECK_CONSENT_TEXT = SIMPLECHECK_CONSENT_PARAGRAPHS.join('\n\n');
export const SIMPLECHECK_CONSENT_DATE_FIELD_NAME = 'SimpleCheck Consent Date';
export const SIMPLECHECK_CONSENT_VERSION_FIELD_NAME = 'SimpleCheck Consent Version';
export const SIMPLECHECK_CONSENT_IP_FIELD_NAME = 'SimpleCheck IP Address';
